import { ZoneType } from '@alpinehomeair/common';
import {
  GridSpaceDataType,
  PositionAndDimensionsType,
  PositionType,
  ZoneOutlineData,
  ZoneSpaceShape
} from 'data/types';
import { Polygon, union } from 'polygon-clipping';
import { resolvePhotoCenterInSpaces } from 'utils/space-label-photo-layout';

export type { Obstacle } from 'utils/zone-label-obstacles';

export const ZONE_LABEL_WIDTH = 160;
export const ZONE_LABEL_HEIGHT_NO_BTUS = 24;
export const ZONE_LABEL_HEIGHT_WITH_BTUS = 50;

type Point = { x: number; y: number };

function getZoneSpacesCenter(spacePositions: PositionAndDimensionsType[]): {
  x: number;
  y: number;
} {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  spacePositions.forEach((space) => {
    minX = Math.min(minX, space.x);
    minY = Math.min(minY, space.y);
    maxX = Math.max(maxX, space.x + space.width);
    maxY = Math.max(maxY, space.y + space.height);
  });
  const center = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };

  const isCenterInsideZone = spacePositions.some(
    (space) =>
      center.x >= space.x &&
      center.x <= space.x + space.width &&
      center.y >= space.y &&
      center.y <= space.y + space.height
  );
  if (isCenterInsideZone) return center;

  const largest = spacePositions.reduce((winner, current) =>
    current.width * current.height > winner.width * winner.height
      ? current
      : winner
  );
  return {
    x: largest.x + largest.width / 2,
    y: largest.y + largest.height / 2
  };
}

export function getZonePhotoCenter(
  zoneData: ZoneOutlineData,
  gridSpaces: GridSpaceDataType[] = []
): { x: number; y: number } | null {
  if (!zoneData.airHandlerPhotoUrl) return null;
  if (zoneData.airHandlerPositionPx) return zoneData.airHandlerPositionPx;
  if (zoneData.spacePositions.length === 0) return null;
  const preferred = getZoneSpacesCenter(zoneData.spacePositions);
  if (gridSpaces.length === 0) return preferred;
  return resolvePhotoCenterInSpaces(preferred, gridSpaces);
}

export function formatAirHandlerDescriptor(
  value: string | undefined
): string | undefined {
  if (!value) return undefined;
  const cleaned = value
    .replace(/\s*(Traditional\s+)?Mini\s+Indoor\s+Unit\s*/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned.length > 0 ? cleaned : undefined;
}

function rectVertices(position: PositionAndDimensionsType): PositionType[] {
  const { x, y, width, height } = position;
  return [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + height },
    { x, y: y + height }
  ];
}

function getShapeAbsoluteVertices(shape: ZoneSpaceShape): PositionType[] {
  if (shape.absoluteVertices.length >= 3) return shape.absoluteVertices;
  return rectVertices(shape.position);
}

export function computePerimeter(shapes: ZoneSpaceShape[]): Point[][] {
  if (shapes.length === 0) return [];

  const polygons: Polygon[] = shapes.map((shape) => {
    const verts = getShapeAbsoluteVertices(shape);
    const ring = verts.map((v) => [v.x, v.y] as [number, number]);
    return [[...ring, ring[0]]];
  });

  const [first, ...rest] = polygons;
  const merged = union(first, ...rest);

  return merged.flatMap((poly) => {
    const outer = poly[0];
    if (!outer || outer.length < 4) return [];
    const points = outer.slice(0, -1).map(([x, y]) => ({ x, y }));
    return points.length >= 3 ? [points] : [];
  });
}

export function getSpaceCornerPoints(shapes: ZoneSpaceShape[]): Point[] {
  const points: Point[] = [];
  for (const shape of shapes) {
    const verts = getShapeAbsoluteVertices(shape);
    for (const v of verts) {
      points.push({ x: v.x, y: v.y });
    }
  }
  return points;
}

export function createPerimeterPath(
  paths: Point[][],
  cornerRadius: number = 6
): string {
  if (paths.length === 0) return '';

  const allPathParts: string[] = [];

  for (const path of paths) {
    if (path.length >= 3) {
      const pathParts: string[] = [];
      const n = path.length;

      for (let i = 0; i < n; i += 1) {
        const prev = path[(i - 1 + n) % n];
        const curr = path[i];
        const next = path[(i + 1) % n];

        const toPrev = { x: prev.x - curr.x, y: prev.y - curr.y };
        const toNext = { x: next.x - curr.x, y: next.y - curr.y };

        const lenPrev = Math.hypot(toPrev.x, toPrev.y);
        const lenNext = Math.hypot(toNext.x, toNext.y);

        if (lenPrev !== 0 && lenNext !== 0) {
          const radius = Math.min(cornerRadius, lenPrev / 2, lenNext / 2);

          const unitPrev = { x: toPrev.x / lenPrev, y: toPrev.y / lenPrev };
          const unitNext = { x: toNext.x / lenNext, y: toNext.y / lenNext };

          const startPoint = {
            x: curr.x + unitPrev.x * radius,
            y: curr.y + unitPrev.y * radius
          };
          const endPoint = {
            x: curr.x + unitNext.x * radius,
            y: curr.y + unitNext.y * radius
          };

          if (i === 0) {
            pathParts.push(`M ${startPoint.x} ${startPoint.y}`);
          } else {
            pathParts.push(`L ${startPoint.x} ${startPoint.y}`);
          }

          pathParts.push(`Q ${curr.x} ${curr.y} ${endPoint.x} ${endPoint.y}`);
        }
      }

      if (pathParts.length > 0) {
        const firstPrev = path[n - 1];
        const firstCurr = path[0];
        const toPrevFirst = {
          x: firstPrev.x - firstCurr.x,
          y: firstPrev.y - firstCurr.y
        };
        const lenPrevFirst = Math.hypot(toPrevFirst.x, toPrevFirst.y);
        if (lenPrevFirst > 0) {
          const radiusFirst = Math.min(cornerRadius, lenPrevFirst / 2);
          const unitPrevFirst = {
            x: toPrevFirst.x / lenPrevFirst,
            y: toPrevFirst.y / lenPrevFirst
          };
          const closingPoint = {
            x: firstCurr.x + unitPrevFirst.x * radiusFirst,
            y: firstCurr.y + unitPrevFirst.y * radiusFirst
          };
          pathParts.push(`L ${closingPoint.x} ${closingPoint.y}`);
        }
        pathParts.push('Z');
        allPathParts.push(pathParts.join(' '));
      }
    }
  }

  return allPathParts.join(' ');
}

export type LabelPosition = {
  x: number;
  y: number;
  anchor: 'start' | 'middle' | 'end';
  placement: 'top' | 'bottom' | 'left' | 'right';
};

export function getLabelBounds(
  labelX: number,
  labelY: number,
  labelWidth: number,
  labelHeight: number,
  anchor: 'start' | 'middle' | 'end',
  placement: 'top' | 'bottom' | 'left' | 'right'
): { left: number; top: number; right: number; bottom: number } {
  let left = labelX;
  if (anchor === 'middle') left = labelX - labelWidth / 2;
  else if (anchor === 'end') left = labelX - labelWidth;

  let top = labelY;
  if (placement === 'top') top = labelY - labelHeight;
  else if (placement === 'left' || placement === 'right')
    top = labelY - labelHeight / 2;

  return { left, top, right: left + labelWidth, bottom: top + labelHeight };
}

export function calculateZoneTotalBTUs(zone: ZoneType): {
  heating: number;
  cooling: number;
} {
  return zone.zoneItems.reduce(
    (acc, item) => ({
      heating: acc.heating + (item.BTUs?.heating ?? 0),
      cooling: acc.cooling + (item.BTUs?.cooling ?? 0)
    }),
    { heating: 0, cooling: 0 }
  );
}

export function getZoneSpaceShapes(
  zone: ZoneType,
  gridSpaces: GridSpaceDataType[]
): ZoneSpaceShape[] {
  const spaceIds = new Set(zone.zoneItems.map((item) => item.structureItemId));
  return gridSpaces
    .filter((space) => spaceIds.has(space.spaceId))
    .map((space) => {
      const relativeVertices = space.vertices ?? [];
      const absoluteVertices: PositionType[] =
        relativeVertices.length >= 3
          ? relativeVertices.map((v) => ({
              x: v.x + space.position.x,
              y: v.y + space.position.y
            }))
          : rectVertices(space.position);
      return {
        position: space.position,
        absoluteVertices
      };
    });
}

export function generateZoneColor(zoneIndex: number): {
  outline: string;
  text: string;
} {
  const baseColors = [
    { hue: 210, saturation: 70, lightness: 45 },
    { hue: 30, saturation: 80, lightness: 45 },
    { hue: 150, saturation: 65, lightness: 38 },
    { hue: 280, saturation: 65, lightness: 50 },
    { hue: 55, saturation: 75, lightness: 42 },
    { hue: 180, saturation: 60, lightness: 40 },
    { hue: 320, saturation: 60, lightness: 50 },
    { hue: 0, saturation: 70, lightness: 45 },
    { hue: 90, saturation: 65, lightness: 40 },
    { hue: 240, saturation: 65, lightness: 50 },
    { hue: 350, saturation: 70, lightness: 45 },
    { hue: 120, saturation: 60, lightness: 38 }
  ];

  let color;
  if (zoneIndex < baseColors.length) {
    color = baseColors[zoneIndex];
  } else {
    const hueStep = 360 / Math.max(zoneIndex + 1, baseColors.length);
    const hue = (zoneIndex * hueStep) % 360;
    const saturation = 65 + (zoneIndex % 3) * 5;
    const lightness = 40 + (zoneIndex % 4) * 3;
    color = {
      hue: Math.round(hue),
      saturation: Math.max(60, Math.min(80, saturation)),
      lightness: Math.max(38, Math.min(50, lightness))
    };
  }

  return {
    outline: `hsl(${color.hue}, ${color.saturation}%, ${color.lightness}%)`,
    text: `hsl(${color.hue}, ${color.saturation + 10}%, ${
      color.lightness - 10
    }%)`
  };
}
